import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { subscribeToPush } from '../services/pushNotifications';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroupState] = useState(() => {
    const saved = localStorage.getItem('selectedGroup');
    return saved ? JSON.parse(saved) : null;
  });

  const setSelectedGroup = (group) => {
    setSelectedGroupState(group);
    if (group) {
      localStorage.setItem('selectedGroup', JSON.stringify(group));
    } else {
      localStorage.removeItem('selectedGroup');
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
      subscribeToPush();
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    const result = await api.register(email, password, name);
    return result;
  };

  const verifyEmail = async (email, code) => {
    const result = await api.verifyEmail(email, code);
    if (result.user) {
      setUser(result.user);
      subscribeToPush();
    }
    return result;
  };

  const login = async (email, password) => {
    const userData = await api.login(email, password);
    setUser(userData);
    subscribeToPush();
    return userData;
  };

  const adminLogin = async (username, password) => {
    const userData = await api.adminLogin(username, password);
    setUser(userData);
    subscribeToPush();
    return userData;
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    setSelectedGroup(null);
    localStorage.removeItem('selectedGroup');
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      selectedGroup,
      setSelectedGroup,
      register,
      verifyEmail,
      login,
      adminLogin,
      logout,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
